/**
 * Tiny fetch wrapper for the DUX CRM FastAPI backend.
 *
 * Authentication is cookie-based (httpOnly session cookie set by
 * /api/auth/microsoft/callback or /api/auth/skill-exchange). No API key
 * or token handling needed in JS. Throws on non-2xx responses with a
 * readable error.
 */

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function errorDetail(e: unknown): unknown {
  if (e instanceof ApiError && e.body && typeof e.body === "object") {
    return (e.body as { detail?: unknown }).detail;
  }
  return undefined;
}

/**
 * Human-readable message for a failed request: prefers the FastAPI
 * `{detail: "..."}` body (e.g. a 409's blocker explanation) — or the
 * structured `{detail: {error, message}}` shape — over the generic
 * "<METHOD> <path> failed: <status>" wrapper.
 */
export function apiErrorDetail(e: unknown): string {
  const detail = errorDetail(e);
  if (typeof detail === "string") return detail;
  if (detail && typeof detail === "object") {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }
  return e instanceof Error ? e.message : String(e);
}

/** The machine code of a structured `{detail: {error: code}}` body, if any. */
export function apiErrorCode(e: unknown): string | null {
  const detail = errorDetail(e);
  if (detail && typeof detail === "object") {
    const code = (detail as { error?: unknown }).error;
    if (typeof code === "string") return code;
  }
  return null;
}

/** Fires on 401 so the auth guard can react. */
const authErrorEvent = new Event("auth:expired");

async function request<T>(
  method: string,
  path: string,
  {
    params,
    body,
    signal,
  }: {
    params?: Record<string, string | number | boolean | undefined | null>;
    body?: unknown;
    signal?: AbortSignal;
  } = {},
): Promise<T> {
  let url = path;
  if (params) {
    const qs = Object.entries(params)
      .filter(([, v]) => v !== undefined && v !== null && v !== "")
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
      .join("&");
    if (qs) url += `?${qs}`;
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  // CSRF mitigation: state-changing requests must carry this header. The
  // backend rejects mutating /api/v1/* requests that lack it. Custom headers
  // also force a CORS preflight, which /api/v1/* refuses — blocking any
  // cross-origin forgery even if the session cookie were attached.
  if (method !== "GET" && method !== "HEAD") {
    headers["X-Requested-With"] = "webui";
  }

  const res = await fetch(url, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (res.status === 401) {
    window.dispatchEvent(authErrorEvent);
  }

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const payload = isJson ? await res.json().catch(() => null) : await res.text();

  if (!res.ok) {
    throw new ApiError(
      `${method} ${path} failed: ${res.status}`,
      res.status,
      payload,
    );
  }
  return payload as T;
}

export const api = {
  get: <T>(path: string, params?: Record<string, unknown>, signal?: AbortSignal) =>
    request<T>("GET", path, { params: params as never, signal }),
  post: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>("POST", path, { body, signal }),
  patch: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>("PATCH", path, { body, signal }),
  put: <T>(path: string, body?: unknown, signal?: AbortSignal) =>
    request<T>("PUT", path, { body, signal }),
  delete: <T>(path: string, signal?: AbortSignal) =>
    request<T>("DELETE", path, { signal }),
};
